import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import useProductConfigHeaderImage from '@hooks/images/useProductConfigHeaderImage';
import Image from '@components/Image';
import FixedHeader from '@components/FixedHeader';
import MagicHeader from '@components/MagicHeader';
import useIsDesktopLayout from '@hooks/useIsDesktopLayout';
import headerPlaceholder from '@assets/images/loading-header.png';

import HeaderDescription from '../HeaderDescription';
import Container from './components/Container';
import SecondaryContainer from './components/SecondaryContainer';
import Close from './components/Close';
import Overlay from './components/Overlay';

function ProductConfigurationHeader({
  onClose,
  withOverlay,
  children,
  product,
  isQC = false,
}) {
  const {
    description,
    priceWithDiscount,
    subsidizedProduct,
    price,
    name,
    image,
    hasCustomPhoto,
    discount,
    finalPriceWithoutTaxes,
  } = product;
  const isDesktopLayout = useIsDesktopLayout();
  const [imgLoaded, setImageLoaded] = useState(false);
  const headerImage = useProductConfigHeaderImage({ image, withOverlay, isQC });
  const imageClass = useMemo(
    () =>
      classNames({
        header_image_shelves: withOverlay && imgLoaded,
        header_image: !withOverlay || !imgLoaded,
      }),
    [imgLoaded, withOverlay],
  );

  const onImgLoad = useCallback(e => {
    if (e.target.src !== headerPlaceholder) setImageLoaded(true);
  }, []);
  return (
    <>
      {isDesktopLayout ? (
        <>
          {!hasCustomPhoto && (
            <FixedHeader onClose={onClose} label={name} isModal />
          )}
          <SecondaryContainer withCustomPhoto={hasCustomPhoto}>
            {hasCustomPhoto && (
              <Close onClick={onClose} withOverlay={withOverlay} />
            )}
            <Image
              src={headerImage}
              placeholder={headerPlaceholder}
              customClass={imageClass}
              onLoad={onImgLoad}
              alt="header"
              showErrorImage
            />
          </SecondaryContainer>

          <HeaderDescription
            description={description}
            name={name}
            price={price}
            priceWithDiscount={priceWithDiscount}
            subsidizedProduct={subsidizedProduct}
            discount={discount}
            finalPriceWithoutTaxes={finalPriceWithoutTaxes}
          />
        </>
      ) : (
        <MagicHeader
          mainHeader={
            <>
              <Container className="full_width">
                <Close onClick={onClose} withOverlay={withOverlay} />

                {!withOverlay && <Overlay />}

                <Image
                  src={headerImage}
                  placeholder={headerPlaceholder}
                  customClass={imageClass}
                  onLoad={onImgLoad}
                  alt="header"
                  showErrorImage
                />
              </Container>
              {children}
              <HeaderDescription
                description={description}
                name={name}
                price={price}
                priceWithDiscount={priceWithDiscount}
                subsidizedProduct={subsidizedProduct}
                discount={discount}
                finalPriceWithoutTaxes={finalPriceWithoutTaxes}
              />
            </>
          }
          secondHeader={<FixedHeader onClose={onClose} label={name} />}
        />
      )}
    </>
  );
}

ProductConfigurationHeader.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    image: PropTypes.string.isRequired,
    price: PropTypes.number,
    priceWithDiscount: PropTypes.number,
    subsidizedProduct: PropTypes.object,
    itemId: PropTypes.string,
    isFavourite: PropTypes.bool,
    hasCustomPhoto: PropTypes.bool,
    discount: PropTypes.number,
    finalPriceWithoutTaxes: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  withOverlay: PropTypes.bool.isRequired,
  children: PropTypes.node,
  isQC: PropTypes.bool,
};

export default ProductConfigurationHeader;
